import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { FaEdit, FaTrash, FaPlus, FaTimes, FaCheck, FaSpinner, FaEye, FaEyeSlash } from 'react-icons/fa';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GOAL_OPTIONS = [
  { value: 'all', label: 'Todos los objetivos' },
  { value: 'fat_loss', label: 'Perder grasa' },
  { value: 'muscle_gain', label: 'Ganar masa muscular' },
  { value: 'recomposition', label: 'Recomposición corporal' },
  { value: 'maintain', label: 'Mantener peso' },
];
const LEVEL_OPTIONS = [
  { value: 'all', label: 'Todos los niveles' },
  { value: 'beginner', label: 'Principiante' },
  { value: 'intermediate', label: 'Intermedia' },
  { value: 'advanced', label: 'Avanzada' },
];
const PLAN_OPTIONS = [
  { value: 'all', label: 'Todas las clientas' },
  { value: 'basic', label: 'Fit Evolution' },
  { value: 'premium', label: 'Asesoría 1 a 1 (Premium)' },
  { value: 'gold', label: 'Plan VIP (Gold)' },
  { value: 'vip_gold', label: 'Gold VIP' },
  { value: 'competitivo', label: 'Competitivo' },
];

const SEND_MODE_OPTIONS = [
  { value: 'broadcast', label: '⚡ Envío inmediato (a todas las clientas del filtro)' },
  { value: 'scheduled', label: '📅 Agendado (semana + día del proceso)' },
];

const emptyForm = {
  title: '',
  body: '',
  icon: '💪',
  goal_filter: 'all',
  level_filter: 'all',
  plan_filter: 'all',
  week_number: 1,
  day_in_week: 1,
  active: true,
  send_mode: 'broadcast',
  target_emails_raw: '',
  repeat_interval: 0,
  repeat_unit: '',
};

/**
 * Admin Manager para crear/editar mensajes de la coach.
 * Los mensajes se envían automáticamente al dashboard de cada clienta según
 * su semana/día calculado desde subscription_start_date.
 */
export default function CoachMessagesManager() {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null); // 'new' o el id
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [filterWeek, setFilterWeek] = useState('all');

  const fetchList = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('deisycfit_token');
      const { data } = await axios.get(`${API}/coach-messages/admin/list`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      setMessages(data?.items || []);
    } catch (e) {
      toast.error('Error cargando mensajes: ' + (e.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchList(); }, []);

  const openNew = () => {
    setForm(emptyForm);
    setEditingId('new');
  };

  const openEdit = (msg) => {
    setForm({
      title: msg.title,
      body: msg.body,
      icon: msg.icon || '💪',
      goal_filter: msg.goal_filter || 'all',
      level_filter: msg.level_filter || 'all',
      plan_filter: msg.plan_filter || 'all',
      week_number: msg.week_number || 1,
      day_in_week: msg.day_in_week || 1,
      active: msg.active !== false,
      send_mode: msg.send_mode || 'scheduled',
      target_emails_raw: (msg.target_emails || []).join(', '),
      repeat_interval: Number(msg.repeat_interval) || 0,
      repeat_unit: msg.repeat_unit || '',
    });
    setEditingId(msg.id);
  };

  const save = async () => {
    if (!form.title.trim() || !form.body.trim()) {
      toast.error('Título y cuerpo son obligatorios');
      return;
    }
    setSaving(true);
    try {
      const token = localStorage.getItem('deisycfit_token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const target_emails = (form.send_mode === 'broadcast')
        ? (form.target_emails_raw || '').split(/[\s,;]+/).map((s) => s.trim().toLowerCase()).filter(Boolean)
        : [];
      const payload = {
        title: form.title,
        body: form.body,
        icon: form.icon,
        goal_filter: form.goal_filter,
        level_filter: form.level_filter,
        plan_filter: form.plan_filter,
        week_number: form.week_number,
        day_in_week: form.day_in_week,
        active: form.active,
        send_mode: form.send_mode,
        target_emails,
        repeat_interval: Number(form.repeat_interval) || 0,
        repeat_unit: form.repeat_unit || null,
      };
      if (editingId === 'new') {
        await axios.post(`${API}/coach-messages/admin/create`, payload, { headers });
        toast.success(form.send_mode === 'broadcast' ? '📨 Mensaje enviado a las clientas' : 'Mensaje agendado');
      } else {
        await axios.put(`${API}/coach-messages/admin/${editingId}`, payload, { headers });
        toast.success('Mensaje actualizado');
      }
      setEditingId(null);
      fetchList();
    } catch (e) {
      toast.error('Error: ' + (e.response?.data?.detail || e.message));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (msg) => {
    if (!window.confirm(`¿Eliminar "${msg.title}"? Esta acción no se puede deshacer.`)) return;
    try {
      const token = localStorage.getItem('deisycfit_token');
      await axios.delete(`${API}/coach-messages/admin/${msg.id}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      toast.success('Mensaje eliminado');
      fetchList();
    } catch (e) {
      toast.error('Error: ' + (e.response?.data?.detail || e.message));
    }
  };

  const toggleActive = async (msg) => {
    try {
      const token = localStorage.getItem('deisycfit_token');
      await axios.put(`${API}/coach-messages/admin/${msg.id}`, { ...msg, active: !msg.active }, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      fetchList();
    } catch (e) {
      toast.error('Error: ' + (e.response?.data?.detail || e.message));
    }
  };

  // Agrupar: broadcasts en un grupo aparte, scheduled por semana
  const filtered = filterWeek === 'all' ? messages : messages.filter((m) => (m.send_mode !== 'broadcast') && m.week_number === parseInt(filterWeek));
  const broadcasts = filtered.filter((m) => m.send_mode === 'broadcast')
    .sort((a, b) => (b.created_at || '').localeCompare(a.created_at || ''));
  const scheduled = filtered.filter((m) => m.send_mode !== 'broadcast');
  const byWeek = {};
  scheduled.forEach((m) => {
    if (!byWeek[m.week_number]) byWeek[m.week_number] = [];
    byWeek[m.week_number].push(m);
  });
  const weeks = Object.keys(byWeek).sort((a, b) => parseInt(a) - parseInt(b));
  const availableWeeks = [...new Set(messages.filter(m => m.send_mode !== 'broadcast').map((m) => m.week_number))].sort((a, b) => a - b);

  const goalLabel = (v) => GOAL_OPTIONS.find((o) => o.value === v)?.label || v;
  const levelLabel = (v) => LEVEL_OPTIONS.find((o) => o.value === v)?.label || v;
  const planLabel = (v) => PLAN_OPTIONS.find((o) => o.value === v)?.label || v || 'Todas las cuentas';
  const dayLabel = (d) => ['Día 1', 'Día 2', 'Día 3', 'Día 4', 'Día 5', 'Día 6', 'Día 7'][d - 1] || `Día ${d}`;

  return (
    <div className="space-y-4" data-testid="coach-messages-manager">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-xl font-bold text-white flex items-center gap-2">
            💬 Mensajes de tu Coach
          </h2>
          <p className="text-xs text-slate-400 mt-1">
            Envía consejos personalizados a tus clientas según su semana y objetivo. Se muestran automáticamente al abrir la app.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={filterWeek}
            onChange={(e) => setFilterWeek(e.target.value)}
            className="px-3 py-2 rounded bg-slate-800 text-white text-sm border border-slate-700"
            data-testid="coach-msg-filter-week"
          >
            <option value="all">Todas las semanas</option>
            {availableWeeks.map((w) => (
              <option key={w} value={w}>Semana {w}</option>
            ))}
          </select>
          <button
            onClick={openNew}
            className="px-4 py-2 rounded bg-pink-500 text-white text-sm font-semibold hover:bg-pink-600 flex items-center gap-2"
            data-testid="coach-msg-new-btn"
          >
            <FaPlus /> Nuevo mensaje
          </button>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-10 text-slate-400">
          <FaSpinner className="animate-spin inline text-2xl" />
        </div>
      ) : (weeks.length === 0 && broadcasts.length === 0) ? (
        <div className="text-center py-10 text-slate-500 rounded-xl bg-slate-800/40 border border-dashed border-slate-700">
          <p className="text-sm">Todavía no has creado mensajes.</p>
          <p className="text-xs mt-1">Crea un mensaje inmediato o agéndalo para una semana específica.</p>
        </div>
      ) : (
        <div className="space-y-6">
          {broadcasts.length > 0 && (
            <div className="rounded-xl p-4" style={{ background: 'rgba(6, 182, 212, 0.06)', border: '1px solid rgba(6, 182, 212, 0.25)' }}>
              <h3 className="text-cyan-300 font-bold mb-3">⚡ Envíos inmediatos (broadcast)</h3>
              <div className="grid md:grid-cols-2 gap-2">
                {broadcasts.map((msg) => (
                  <div
                    key={msg.id}
                    className={`rounded-lg p-3 border ${msg.active ? 'bg-slate-800/60 border-white/10' : 'bg-slate-900/60 border-slate-700 opacity-60'}`}
                    data-testid={`coach-msg-card-${msg.id}`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-1.5 mb-1 flex-wrap">
                          <span className="text-lg">{msg.icon || '💪'}</span>
                          <span className="text-[10px] uppercase tracking-wider font-bold text-cyan-300 bg-cyan-500/10 px-1.5 py-0.5 rounded border border-cyan-400/30">
                            ⚡ Inmediato
                          </span>
                          <span className="text-[10px] text-cyan-300 bg-cyan-500/10 px-1.5 py-0.5 rounded">{goalLabel(msg.goal_filter)}</span>
                          <span className="text-[10px] text-purple-300 bg-purple-500/10 px-1.5 py-0.5 rounded">{levelLabel(msg.level_filter)}</span>
                          <span className="text-[10px] text-amber-300 bg-amber-500/10 px-1.5 py-0.5 rounded">{planLabel(msg.plan_filter)}</span>
                          {(msg.target_emails || []).length > 0 && (
                            <span className="text-[10px] text-emerald-300 bg-emerald-500/10 px-1.5 py-0.5 rounded border border-emerald-400/30">
                              👤 {msg.target_emails.length} correo{msg.target_emails.length === 1 ? '' : 's'}
                            </span>
                          )}
                        </div>
                        <h4 className="text-white text-sm font-semibold truncate">{msg.title}</h4>
                        <p className="text-slate-400 text-xs mt-0.5 line-clamp-2">{msg.body}</p>
                      </div>
                      <div className="flex flex-col gap-1 flex-shrink-0">
                        <button
                          onClick={() => toggleActive(msg)}
                          className="w-7 h-7 rounded flex items-center justify-center bg-slate-700/60 text-slate-300 hover:bg-slate-700"
                          title={msg.active ? 'Desactivar' : 'Activar'}
                        >
                          {msg.active ? <FaEye className="text-xs" /> : <FaEyeSlash className="text-xs" />}
                        </button>
                        <button
                          onClick={() => openEdit(msg)}
                          className="w-7 h-7 rounded flex items-center justify-center bg-cyan-500/20 text-cyan-300 hover:bg-cyan-500/30"
                          title="Editar"
                          data-testid={`coach-msg-edit-${msg.id}`}
                        >
                          <FaEdit className="text-xs" />
                        </button>
                        <button
                          onClick={() => remove(msg)}
                          className="w-7 h-7 rounded flex items-center justify-center bg-red-500/20 text-red-300 hover:bg-red-500/30"
                          title="Eliminar"
                          data-testid={`coach-msg-delete-${msg.id}`}
                        >
                          <FaTrash className="text-xs" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {weeks.map((w) => (
            <div key={w} className="rounded-xl p-4" style={{ background: 'rgba(var(--brand-primary-glow), 0.06)', border: '1px solid rgba(var(--brand-primary-glow), 0.2)' }}>
              <h3 className="text-pink-300 font-bold mb-3">🗓️ Semana {w}</h3>
              <div className="grid md:grid-cols-2 gap-2">
                {byWeek[w].sort((a, b) => a.day_in_week - b.day_in_week).map((msg) => (
                  <div
                    key={msg.id}
                    className={`rounded-lg p-3 border ${msg.active ? 'bg-slate-800/60 border-white/10' : 'bg-slate-900/60 border-slate-700 opacity-60'}`}
                    data-testid={`coach-msg-card-${msg.id}`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-1.5 mb-1 flex-wrap">
                          <span className="text-lg">{msg.icon || '💪'}</span>
                          {msg.send_mode === 'broadcast' ? (
                            <span className="text-[10px] uppercase tracking-wider font-bold text-cyan-300 bg-cyan-500/10 px-1.5 py-0.5 rounded border border-cyan-400/30">
                              ⚡ Inmediato
                            </span>
                          ) : (
                            <span className="text-[10px] uppercase tracking-wider text-pink-400 font-bold">{dayLabel(msg.day_in_week)}</span>
                          )}
                          <span className="text-[10px] text-cyan-300 bg-cyan-500/10 px-1.5 py-0.5 rounded">{goalLabel(msg.goal_filter)}</span>
                          <span className="text-[10px] text-purple-300 bg-purple-500/10 px-1.5 py-0.5 rounded">{levelLabel(msg.level_filter)}</span>
                          <span className="text-[10px] text-amber-300 bg-amber-500/10 px-1.5 py-0.5 rounded">{planLabel(msg.plan_filter)}</span>
                          {(msg.target_emails || []).length > 0 && (
                            <span className="text-[10px] text-emerald-300 bg-emerald-500/10 px-1.5 py-0.5 rounded border border-emerald-400/30">
                              👤 {msg.target_emails.length} correo{msg.target_emails.length === 1 ? '' : 's'}
                            </span>
                          )}
                        </div>
                        <h4 className="text-white text-sm font-semibold truncate">{msg.title}</h4>
                        <p className="text-slate-400 text-xs mt-0.5 line-clamp-2">{msg.body}</p>
                      </div>
                      <div className="flex flex-col gap-1 flex-shrink-0">
                        <button
                          onClick={() => toggleActive(msg)}
                          className="w-7 h-7 rounded flex items-center justify-center bg-slate-700/60 text-slate-300 hover:bg-slate-700"
                          title={msg.active ? 'Desactivar' : 'Activar'}
                        >
                          {msg.active ? <FaEye className="text-xs" /> : <FaEyeSlash className="text-xs" />}
                        </button>
                        <button
                          onClick={() => openEdit(msg)}
                          className="w-7 h-7 rounded flex items-center justify-center bg-cyan-500/20 text-cyan-300 hover:bg-cyan-500/30"
                          title="Editar"
                          data-testid={`coach-msg-edit-${msg.id}`}
                        >
                          <FaEdit className="text-xs" />
                        </button>
                        <button
                          onClick={() => remove(msg)}
                          className="w-7 h-7 rounded flex items-center justify-center bg-red-500/20 text-red-300 hover:bg-red-500/30"
                          title="Eliminar"
                          data-testid={`coach-msg-delete-${msg.id}`}
                        >
                          <FaTrash className="text-xs" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {editingId && (
        <div
          className="fixed inset-0 z-[110] flex items-center justify-center p-4"
          style={{ background: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(6px)' }}
          onClick={() => setEditingId(null)}
        >
          <div
            className="w-full max-w-lg rounded-2xl p-5 overflow-y-auto max-h-[90vh]"
            style={{ background: 'linear-gradient(180deg, #0f172a, #1e1b4b)', border: '1px solid rgba(255,255,255,0.1)' }}
            onClick={(e) => e.stopPropagation()}
            data-testid="coach-msg-editor"
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-white font-bold">
                {editingId === 'new' ? '✨ Nuevo mensaje' : '✏️ Editar mensaje'}
              </h3>
              <button
                onClick={() => setEditingId(null)}
                className="w-8 h-8 rounded-full flex items-center justify-center text-white hover:bg-white/10"
              >
                <FaTimes />
              </button>
            </div>

            <div className="space-y-3">
              <div className="grid grid-cols-4 gap-2">
                <div className="col-span-1">
                  <label className="block text-[11px] text-slate-400 mb-1 uppercase tracking-wider">Ícono</label>
                  <input
                    type="text"
                    value={form.icon}
                    onChange={(e) => setForm({ ...form, icon: e.target.value })}
                    className="w-full px-2 py-2 rounded bg-slate-900 text-white text-lg text-center border border-slate-700"
                    placeholder="💪"
                    maxLength={4}
                    data-testid="coach-msg-icon"
                  />
                </div>
                <div className="col-span-3">
                  <label className="block text-[11px] text-slate-400 mb-1 uppercase tracking-wider">Título</label>
                  <input
                    type="text"
                    value={form.title}
                    onChange={(e) => setForm({ ...form, title: e.target.value })}
                    className="w-full px-3 py-2 rounded bg-slate-900 text-white text-sm border border-slate-700"
                    placeholder="Ej: ¡Bienvenida a tu Semana 1!"
                    data-testid="coach-msg-title"
                  />
                </div>
              </div>

              <div>
                <label className="block text-[11px] text-slate-400 mb-1 uppercase tracking-wider">Mensaje</label>
                <textarea
                  value={form.body}
                  onChange={(e) => setForm({ ...form, body: e.target.value })}
                  rows={5}
                  className="w-full px-3 py-2 rounded bg-slate-900 text-white text-sm border border-slate-700"
                  placeholder="Escribe aquí el consejo o mensaje motivacional…"
                  data-testid="coach-msg-body"
                />
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-[11px] text-slate-400 mb-1 uppercase tracking-wider">Objetivo</label>
                  <select
                    value={form.goal_filter}
                    onChange={(e) => setForm({ ...form, goal_filter: e.target.value })}
                    className="w-full px-2 py-2 rounded bg-slate-900 text-white text-sm border border-slate-700"
                    data-testid="coach-msg-goal"
                  >
                    {GOAL_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-[11px] text-slate-400 mb-1 uppercase tracking-wider">Nivel</label>
                  <select
                    value={form.level_filter}
                    onChange={(e) => setForm({ ...form, level_filter: e.target.value })}
                    className="w-full px-2 py-2 rounded bg-slate-900 text-white text-sm border border-slate-700"
                    data-testid="coach-msg-level"
                  >
                    {LEVEL_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-[11px] text-cyan-300 mb-1 uppercase tracking-wider font-bold">
                  ⚡ Modo de envío
                </label>
                <select
                  value={form.send_mode}
                  onChange={(e) => setForm({ ...form, send_mode: e.target.value })}
                  className="w-full px-2 py-2 rounded bg-slate-900 text-white text-sm border border-cyan-600/30"
                  data-testid="coach-msg-send-mode"
                >
                  {SEND_MODE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
                <p className="text-[10px] text-slate-400 mt-1">
                  {form.send_mode === 'broadcast'
                    ? 'Se envía al instante a todas las clientas que coincidan con el filtro. Ideal para avisos.'
                    : 'Se dispara automáticamente cuando la clienta llegue a la semana y día indicados en su proceso.'}
                </p>
              </div>

              <div>
                <label className="block text-[11px] text-amber-300 mb-1 uppercase tracking-wider font-bold">
                  📋 Categoría / Plan destinatario
                </label>
                <select
                  value={form.plan_filter}
                  onChange={(e) => setForm({ ...form, plan_filter: e.target.value })}
                  className="w-full px-2 py-2 rounded bg-slate-900 text-white text-sm border border-amber-600/30"
                  data-testid="coach-msg-plan"
                >
                  {PLAN_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
                <p className="text-[10px] text-slate-400 mt-1">
                  Elige el grupo destinatario. &quot;Todas las clientas&quot; envía a Fit Evolution, Asesoría 1 a 1, VIP, etc.
                </p>
              </div>

              {form.send_mode === 'broadcast' && (
                <div>
                  <label className="block text-[11px] text-emerald-300 mb-1 uppercase tracking-wider font-bold">
                    👤 Correos específicos (opcional)
                  </label>
                  <textarea
                    value={form.target_emails_raw}
                    onChange={(e) => setForm({ ...form, target_emails_raw: e.target.value })}
                    rows={2}
                    className="w-full px-3 py-2 rounded bg-slate-900 text-white text-sm border border-emerald-600/30"
                    placeholder="cliente1@correo.com, cliente2@correo.com"
                    data-testid="coach-msg-target-emails"
                  />
                  <p className="text-[10px] text-slate-400 mt-1">
                    Si escribes correos, el mensaje llegará SOLO a esas clientas (ignora el filtro de plan).
                    Déjalo vacío para enviar por plan/categoría.
                  </p>
                </div>
              )}

              {form.send_mode === 'scheduled' && (
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-[11px] text-slate-400 mb-1 uppercase tracking-wider">Semana</label>
                    <input
                      type="number"
                      min="1"
                      value={form.week_number}
                      onChange={(e) => setForm({ ...form, week_number: parseInt(e.target.value) || 1 })}
                      className="w-full px-3 py-2 rounded bg-slate-900 text-white text-sm border border-slate-700"
                      data-testid="coach-msg-week"
                    />
                  </div>
                  <div>
                    <label className="block text-[11px] text-slate-400 mb-1 uppercase tracking-wider">Día de la semana (1-7)</label>
                    <input
                      type="number"
                      min="1"
                      max="7"
                      value={form.day_in_week}
                      onChange={(e) => setForm({ ...form, day_in_week: Math.max(1, Math.min(7, parseInt(e.target.value) || 1)) })}
                      className="w-full px-3 py-2 rounded bg-slate-900 text-white text-sm border border-slate-700"
                      data-testid="coach-msg-day"
                    />
                  </div>
                </div>
              )}

              {/* Repetición automática (2026-02) */}
              <div className="rounded-lg border border-purple-500/30 bg-purple-500/5 p-3 space-y-2">
                <div className="flex items-center gap-2 text-purple-200 text-xs font-semibold uppercase tracking-wider">
                  🔁 Repetición automática (opcional)
                </div>
                <p className="text-[11px] text-slate-400 -mt-1">
                  Si activas la repetición, el sistema volverá a marcar este mensaje como no leído
                  para las destinatarias cada X tiempo desde el último envío.
                </p>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-[11px] text-slate-400 mb-1 uppercase tracking-wider">Cantidad</label>
                    <input
                      type="number"
                      min="0"
                      value={form.repeat_interval}
                      onChange={(e) => setForm({ ...form, repeat_interval: Math.max(0, parseInt(e.target.value) || 0) })}
                      placeholder="0 = sin repetición"
                      className="w-full px-3 py-2 rounded bg-slate-900 text-white text-sm border border-slate-700"
                      data-testid="coach-msg-repeat-interval"
                    />
                  </div>
                  <div>
                    <label className="block text-[11px] text-slate-400 mb-1 uppercase tracking-wider">Unidad</label>
                    <select
                      value={form.repeat_unit || ''}
                      onChange={(e) => setForm({ ...form, repeat_unit: e.target.value })}
                      className="w-full px-3 py-2 rounded bg-slate-900 text-white text-sm border border-slate-700"
                      data-testid="coach-msg-repeat-unit"
                      disabled={!form.repeat_interval || form.repeat_interval <= 0}
                    >
                      <option value="">— Elegir unidad —</option>
                      <option value="days">Días</option>
                      <option value="weeks">Semanas</option>
                      <option value="months">Meses (≈30 días)</option>
                    </select>
                  </div>
                </div>
              </div>

              <label className="flex items-center gap-2 text-sm text-slate-300 pt-1">
                <input
                  type="checkbox"
                  checked={form.active}
                  onChange={(e) => setForm({ ...form, active: e.target.checked })}
                  className="w-4 h-4 accent-pink-500"
                  data-testid="coach-msg-active"
                />
                Activo (se envía a las clientas)
              </label>

              <div className="flex gap-2 justify-end pt-3 border-t border-white/10">
                <button
                  onClick={() => setEditingId(null)}
                  className="px-4 py-2 rounded bg-slate-700 text-white text-sm hover:bg-slate-600"
                >
                  Cancelar
                </button>
                <button
                  onClick={save}
                  disabled={saving}
                  className="px-4 py-2 rounded bg-pink-500 text-white text-sm font-semibold hover:bg-pink-600 disabled:opacity-50 flex items-center gap-2"
                  data-testid="coach-msg-save"
                >
                  {saving ? <FaSpinner className="animate-spin" /> : <FaCheck />}
                  Guardar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
